import React, { useState, useEffect } from 'react';
import { getLob } from '../../../services/authenticationService';
import Loader from "../../../components/loading/Loader";

interface ProfileFormData {
  lobName: string;
  registeredMobile: string;
  alternateMobile: string;
  registeredEmail: string;
  alternateEmail: string;
  country: string;
  stateRegion: string;
  timezone: string;
  currencySymbol: string;
  languagePreference: string;
}

const ProfileSettings: React.FC = () => {
  const [hasChanges, setHasChanges] = useState(false);
  const [formData, setFormData] = useState<ProfileFormData>({
    lobName: '',
    registeredMobile: '',
    alternateMobile: '',
    registeredEmail: '',
    alternateEmail: '',
    country: '',
    stateRegion: '',
    timezone: 'Select timezone',
    currencySymbol: '',
    languagePreference: 'Select language preference'
  });
  const [loading, setLoading] = useState(true);
  const [originalData, setOriginalData] = useState<ProfileFormData>(formData);
  const [createdTime, setCreatedTime] = useState('');
  const [lastModifiedTime, setLastModifiedTime] = useState('');

  const formatISTDate = (isoDate: string) => {
    const date = new Date(isoDate);
    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }) + ' IST';
  };

  const fetchProfileData = async () => {
    const lob = getLob();
    const url = `https://xly68awt34.execute-api.ap-south-1.amazonaws.com/lob/${lob}`;

    try {
      setLoading(true);
      const response = await fetch(url);
      const data = await response.json();

      const updatedFormData: ProfileFormData = {
        lobName: data.lob || '',
        registeredMobile: '', // not returned by API
        alternateMobile: '',
        registeredEmail: data.workEmail || '',
        alternateEmail: '',
        country: data.deploymentCountry || '',
        stateRegion: '',
        timezone: 'Select timezone',
        currencySymbol: '',
        languagePreference: 'Select language preference'
      };

      setFormData(updatedFormData);
      setOriginalData(updatedFormData);

      if (data.creationTime) setCreatedTime(formatISTDate(data.creationTime));
      if (data.lastModifiedTime) setLastModifiedTime(formatISTDate(data.lastModifiedTime));
    } catch (error) {
      console.error('Failed to fetch profile data:', error);
    }finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfileData();
  }, []);

  const handleInputChange = (field: keyof ProfileFormData, value: string) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);

    const changed = Object.keys(newData).some(
      key => newData[key as keyof ProfileFormData] !== originalData[key as keyof ProfileFormData]
    );
    setHasChanges(changed);
  };

  const handleSave = () => {
    setOriginalData(formData);
    setHasChanges(false);
  };

  const handleCancel = () => {
    setFormData(originalData);
    setHasChanges(false);
  };

  return loading ? (
    <Loader />) :(
    <div className="settings-content">
      {/* Header */}
      <div className="settings-content-header" style={{ borderBottom: 'none' }}>
        <h2 className="settings-content-title">Profile</h2>
        <p className="settings-content-subtitle">
          Manage key studio information and contact details. Some fields can be edited, others are fixed post-setup.
        </p>
      </div>

      {/* Content */}
      <div className="settings-content-body">
        {/* Organization & LOB Info Section */}
        <div className="settings-section">
          <div className="settings-section-header">
            <h2 className="settings-section-title">Organization & LOB Info</h2>
            <p className="settings-section-description">
              View and manage your studio LOB name, deployment type and registration details
            </p>
          </div>

          <div className="settings-dates-container">
            <div className="settings-date-item">
              <div className="settings-date-label">Created on</div>
              <div className="settings-date-value">{createdTime || '—'}</div>
            </div>
            <div className="settings-date-item">
              <div className="settings-date-label">Last modified on</div>
              <div className="settings-date-value">{lastModifiedTime || '—'}</div>
            </div>
          </div>

          <div className="settings-form-grid settings-form-grid-3">
            <div className="settings-form-group">
              <label className="settings-form-label">LOB name</label>
              <input
                type="text"
                value={formData.lobName}
                disabled
                className="settings-form-input"
              />
            </div>

            <div className="settings-form-group">
              <label className="settings-form-label">Registered mobile no.</label>
              <input
                type="text"
                value={formData.registeredMobile}
                onChange={(e) => handleInputChange('registeredMobile', e.target.value)}
                className="settings-form-input"
              />
            </div>

            <div className="settings-form-group">
              <label className="settings-form-label">Alternate mobile no. (Optional)</label>
              <input
                type="text"
                value={formData.alternateMobile}
                onChange={(e) => handleInputChange('alternateMobile', e.target.value)}
                placeholder="Enter alternate mobile no."
                className="settings-form-input"
              />
            </div>
          </div>

          <div className="settings-form-grid settings-form-grid-3">
            <div className="settings-form-group">
              <label className="settings-form-label">Registered email id</label>
              <input
                type="email"
                value={formData.registeredEmail}
                disabled
                className="settings-form-input"
              />
            </div>

            <div className="settings-form-group">
              <label className="settings-form-label">Alternate email id (Optional)</label>
              <input
                type="email"
                value={formData.alternateEmail}
                onChange={(e) => handleInputChange('alternateEmail', e.target.value)}
                placeholder="Enter alternate email id"
                className="settings-form-input"
              />
            </div>
          </div>
        </div>

        {/* Location & Operational Info Section */}
        <div className="settings-section">
          <div className="settings-section-header">
            <h2 className="settings-section-title">Location & Operational Info</h2>
            <p className="settings-section-description">
              Set your operational country, time zone and preferred currency for consistent experience
            </p>
          </div>

          <div className="settings-form-grid settings-form-grid-3">
            <div className="settings-form-group">
              <label className="settings-form-label">Country of operation</label>
              <select
                value={formData.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                className="settings-form-select"
              >
                <option value="India">India</option>
                <option value="USA">USA</option>
                <option value="UK">UK</option>
              </select>
            </div>

            <div className="settings-form-group">
              <label className="settings-form-label">State/Region</label>
              <input
                type="text"
                value={formData.stateRegion}
                onChange={(e) => handleInputChange('stateRegion', e.target.value)}
                placeholder="Enter state/region"
                className="settings-form-input"
              />
            </div>

            <div className="settings-form-group">
              <label className="settings-form-label">Timezone</label>
              <select
                value={formData.timezone}
                onChange={(e) => handleInputChange('timezone', e.target.value)}
                className="settings-form-select"
              >
                <option value="Select timezone">Select timezone</option>
                <option value="IST">IST (UTC+05:30)</option>
                <option value="PST">PST (UTC-08:00)</option>
                <option value="EST">EST (UTC-05:00)</option>
              </select>
            </div>
          </div>

          <div className="settings-form-grid settings-form-grid-3">
            <div className="settings-form-group">
              <label className="settings-form-label">Currency symbol</label>
              <input
                type="text"
                value={formData.currencySymbol}
                onChange={(e) => handleInputChange('currencySymbol', e.target.value)}
                placeholder="Enter currency symbol"
                className="settings-form-input"
              />
            </div>

            <div className="settings-form-group">
              <label className="settings-form-label">Language preference</label>
              <select
                value={formData.languagePreference}
                onChange={(e) => handleInputChange('languagePreference', e.target.value)}
                className="settings-form-select"
              >
                <option value="Select language preference">Select language preference</option>
                <option value="English">English</option>
                <option value="Hindi">Hindi</option>
                <option value="Spanish">Spanish</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {hasChanges && (
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            backgroundColor: '#ffffff',
            borderTop: '1px solid #e0e0e0',
            borderRadius: 6,
            padding: '16px 24px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '16px',
            zIndex: 1000,
            boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
            width: "100%"
          }}
        >
          <button onClick={handleCancel} className="settings-btn settings-btn-secondary">
            Cancel
          </button>
          <button onClick={handleSave} className="settings-btn settings-btn-primary">
            Save Changes
          </button>
        </div>
      )}

    </div>
  );
};

export default ProfileSettings;